import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, BookOpen } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import postsAPI from '../../api/posts.api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0 });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        // Get all posts to show statistics
        const data = await postsAPI.getPosts(1, 100);
        const allPosts = data.posts;

        // Calculate stats - note: API only returns published posts in getPosts
        // For a complete stat, we'd need a separate endpoint
        setStats({
          total: data.pagination.total || 0,
          published: allPosts.filter(p => p.published).length,
          drafts: 0 // This would need a separate API endpoint
        });

        // Get recent posts (first 5)
        setRecentPosts(allPosts.slice(0, 5));
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--navy)',
            margin: 0
          }}>
            Dashboard
          </h1>
          <button
            onClick={() => navigate('/admin/posts/new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'var(--teal)',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            <Plus size={18} />
            New Post
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee8e8',
            border: '1px solid #d96f7b',
            borderRadius: '0.375rem',
            padding: '1rem',
            marginBottom: '2rem',
            color: '#c42a45'
          }}>
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        {!loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '3rem'
          }}>
            {/* Total Posts Card */}
            <div style={{
              background: 'white',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  color: 'var(--text-secondary)',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  margin: 0,
                  textTransform: 'uppercase'
                }}>
                  Total Posts
                </h3>
                <BookOpen size={24} style={{ color: 'var(--teal)' }} />
              </div>
              <p style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--navy)',
                margin: 0
              }}>
                {stats.total}
              </p>
            </div>

            {/* Published Posts Card */}
            <div style={{
              background: 'white',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  color: 'var(--text-secondary)',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  margin: 0,
                  textTransform: 'uppercase'
                }}>
                  Published
                </h3>
                <div style={{
                  background: '#c1f0b6',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  color: '#4a7c3f'
                }}>
                  ✓
                </div>
              </div>
              <p style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--navy)',
                margin: 0
              }}>
                {stats.published}
              </p>
            </div>

            {/* Draft Posts Card */}
            <div style={{
              background: 'white',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  color: 'var(--text-secondary)',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  margin: 0,
                  textTransform: 'uppercase'
                }}>
                  Drafts
                </h3>
                <div style={{
                  background: '#fff3cd',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  color: '#856404'
                }}>
                  ✎
                </div>
              </div>
              <p style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--navy)',
                margin: 0
              }}>
                {stats.drafts}
              </p>
            </div>
          </div>
        )}

        {/* Recent Posts */}
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--navy)',
              margin: 0
            }}>
              Recent Posts
            </h2>
            <a
              href="/admin/posts"
              onClick={(e) => {
                e.preventDefault();
                navigate('/admin/posts');
              }}
              style={{
                color: 'var(--teal)',
                textDecoration: 'none',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              View All →
            </a>
          </div>

          {loading && (
            <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
          )}

          {!loading && recentPosts.length === 0 && (
            <p style={{ color: 'var(--text-secondary)' }}>
              No posts yet. <a
                href="/admin/posts/new"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/admin/posts/new');
                }}
                style={{
                  color: 'var(--teal)',
                  textDecoration: 'none',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create your first post →
              </a>
            </p>
          )}

          {!loading && recentPosts.length > 0 && (
            <div style={{
              borderCollapse: 'collapse',
              width: '100%'
            }}>
              <div style={{
                borderBottom: '2px solid var(--border-color)',
                paddingBottom: '1rem',
                marginBottom: '1rem',
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: '1rem',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem',
                textTransform: 'uppercase'
              }}>
                <div>Title</div>
                <div>Date</div>
                <div>Status</div>
              </div>

              {recentPosts.map((post) => (
                <div
                  key={post.id}
                  style={{
                    borderBottom: '1px solid var(--border-color)',
                    paddingBottom: '1rem',
                    marginBottom: '1rem',
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr',
                    gap: '1rem',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    padding: '1rem'
                  }}
                  onClick={() => navigate(`/admin/posts/${post.id}/edit`)}
                >
                  <div style={{
                    fontWeight: '500',
                    color: 'var(--navy)'
                  }}>
                    {post.title}
                  </div>
                  <div style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.95rem'
                  }}>
                    {new Date(post.publishedAt || post.createdAt).toLocaleDateString()}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    background: post.published ? '#c1f0b6' : '#fff3cd',
                    color: post.published ? '#4a7c3f' : '#856404'
                  }}>
                    {post.published ? 'Published' : 'Draft'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
